import { authFetch } from 'src/auth/utils';
import { ChartTypeValue } from 'src/common/components/ChartTypeToggle/ChartTypeToggle';
import { DoughnutDTO } from 'src/common/components/Dashboard/Types/DashboardApiTypes';
import { UsageTypeValue } from 'src/common/components/UsageTypeSwitch/UsageTypeSwitch';
import { ObjectValues } from 'src/common/tsHelpers';
import { envVariables } from 'src/common/utils/envVariables';
import { UsageDto } from 'src/common/utils/mapUsageDTOtoAreaChartData';
import qs from 'qs';

export const DASHBOARD_SUBSCRIPTION_STATUS = {
  TEST: 'Test',
  ACTIVE: 'Active',
  DEACTIVATED: 'Deactivated',
} as const;

type DashboardSubscriptionStatus = ObjectValues<typeof DASHBOARD_SUBSCRIPTION_STATUS>;

export const RGU_FILTER = {
  ALL: 0,
  SIMS: 1,
  RSIM: 2,
};

export type RGUFilter = ObjectValues<typeof RGU_FILTER>;

type GetDashboardChartDataProps = {
  accountRef: string;
  rguFilter?: RGUFilter;
};

export const getDashboardSubscriptionStatus = async (
  { accountRef, rguFilter }: GetDashboardChartDataProps,
  signal?: AbortSignal,
) => {
  const searchParams: { accountRef: string; rguFilter?: RGUFilter } = {
    accountRef,
    rguFilter,
  };
  const response = await authFetch(
    `${envVariables.VITE_ANALYTICS_MODULE_URL}/usage-tracking/subscription-status?${qs.stringify(
      searchParams,
    )}`,
    {
      signal,
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    },
  );

  const responseJSON: DoughnutDTO<DashboardSubscriptionStatus> = await response.json();

  const data = responseJSON.data.reduce(
    (acc, entry) => {
      acc[entry.label] = entry.value;
      return acc;
    },
    {
      [DASHBOARD_SUBSCRIPTION_STATUS.ACTIVE]: 0,
      [DASHBOARD_SUBSCRIPTION_STATUS.TEST]: 0,
      [DASHBOARD_SUBSCRIPTION_STATUS.DEACTIVATED]: 0,
    },
  );

  return {
    data,
    total: responseJSON.total,
  };
};

export const ACTIVE_IN_SESSION = {
  IN_SESSION: 'In Session',
  OUT_OF_SESSION: 'Out of Session',
} as const;

type DashboardActiveInSession = ObjectValues<typeof ACTIVE_IN_SESSION>;

export const getActiveInSession = async (
  { accountRef }: GetDashboardChartDataProps,
  signal?: AbortSignal,
) => {
  const response = await authFetch(
    `${envVariables.VITE_ANALYTICS_MODULE_URL}/session/state?accountRef=${accountRef}`,
    {
      signal,
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    },
  );

  const responseJSON: DoughnutDTO<DashboardActiveInSession> = await response.json();

  const data = responseJSON.data.reduce(
    (acc, entry) => {
      acc[entry.label] = entry.value;
      return acc;
    },
    {
      [ACTIVE_IN_SESSION.IN_SESSION]: 0,
      [ACTIVE_IN_SESSION.OUT_OF_SESSION]: 0,
    },
  );

  return {
    data,
    total: responseJSON.total,
  };
};

export const DASHBOARD_RSIM_STATUS = {
  PRIMARY: 'Primary',
  BACKUP: 'Backup',
  DISCONNECTED: 'Disconnected',
} as const;

type DashboardRSimStatus = ObjectValues<typeof DASHBOARD_RSIM_STATUS>;

export const getRSimStatus = async (
  { accountRef }: GetDashboardChartDataProps,
  signal?: AbortSignal,
) => {
  const response = await authFetch(
    `${envVariables.VITE_ANALYTICS_MODULE_URL}/inventory/rsims-state?accountRef=${accountRef}`,
    {
      signal,
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    },
  );

  const responseJSON: DoughnutDTO<DashboardRSimStatus> = await response.json();

  const data = responseJSON.data.reduce(
    (acc, entry) => {
      acc[entry.label] = entry.value;
      return acc;
    },
    {
      [DASHBOARD_RSIM_STATUS.PRIMARY]: 0,
      [DASHBOARD_RSIM_STATUS.BACKUP]: 0,
      [DASHBOARD_RSIM_STATUS.DISCONNECTED]: 0,
    },
  );

  return {
    data,
    total: responseJSON.total,
  };
};

type MonthlyUsageDto = {
  unit: string;
  data: { label: string; value: { mt: string; mo: string; total: string } }[];
};

type GetDashboardMonthlyUsageDataProps = GetDashboardChartDataProps & { usageType: UsageTypeValue };

export const getMonthlyUsage = async (
  { accountRef, usageType }: GetDashboardMonthlyUsageDataProps,
  signal?: AbortSignal,
) => {
  const response = await authFetch(
    `${envVariables.VITE_ANALYTICS_MODULE_URL}/usage/${accountRef}/monthly/${usageType}`,
    {
      signal,
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    },
  );

  const responseJSON: MonthlyUsageDto = await response.json();

  return responseJSON;
};

type GetDashboardDailyUsageDataProps = GetDashboardChartDataProps & {
  usageType: UsageTypeValue;
  chartType: ChartTypeValue;
};

export const getDailyUsage = async (
  { accountRef, usageType, chartType }: GetDashboardDailyUsageDataProps,
  signal?: AbortSignal,
) => {
  const response = await authFetch(
    `${envVariables.VITE_ANALYTICS_MODULE_URL}/usage/${accountRef}/daily/${chartType}/${usageType}`,
    {
      signal,
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    },
  );

  const responseJSON: UsageDto = await response.json();

  return responseJSON.chartData;
};

type InventoryDto = {
  unit: string;
  data: {
    label: string;
    value: string;
  }[];
};

export const getInventoryData = async (
  { accountRef }: GetDashboardChartDataProps,
  signal?: AbortSignal,
) => {
  const response = await authFetch(
    `${envVariables.VITE_ANALYTICS_MODULE_URL}/inventory?accountRef=${accountRef}`,
    {
      signal,
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    },
  );

  const responseJSON: InventoryDto = await response.json();

  return responseJSON;
};
